import { Box, useTheme } from '@mui/material';
import { enqueueSnackbar } from '~/utils/notifications';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { MOUSE_BUTTONS_NONE } from '~/engine/tool/MouseButton';
import {
    CrosshairInteractionMode,
    ToolManager,
} from '~/engine/tool/ToolManager';
import { PlaneView, PlaneViewType } from '~/engine/view/views/PlaneView';
import { useCrosshairInteraction } from '~/hooks/useCrosshairInteraction';
import { areEnvironmentsLockedState } from '~/state/atoms/viewerAtoms';

interface CrosshairsProps {
    view: PlaneView | null;
    vertical: number;
    horizontal: number;
    handleCrosshairChange: (
        type: 'horizontal' | 'vertical',
        delta: number
    ) => void;
}

const Crosshairs = ({
    view,
    horizontal,
    vertical,
    handleCrosshairChange,
}: CrosshairsProps) => {
    const theme = useTheme();
    const areEnvironmentsLocked = useRecoilValue(areEnvironmentsLockedState);

    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);
    const boxRef = useRef<HTMLDivElement>(null);
    const dragAbortRef = useRef<AbortController | null>(null);

    // Clear the crosshair-drag flag if unmounted mid-drag (defensive)
    useEffect(() => {
        const boxElement = boxRef.current;
        return () => {
            dragAbortRef.current?.abort();
            dragAbortRef.current = null;
            ToolManager.setCrosshairDragActive(false);
            isDraggingRef.current = false;
            document.body.style.cursor = '';
            const container = boxElement?.closest(
                '[data-viewport-container]'
            ) as HTMLElement | null;
            if (container) {
                container.style.cursor = '';
            }
        };
    }, []);

    const interactionMode = useCrosshairInteraction(view);

    // Whether the line bodies and centre handle accept mouse events.
    const isLineEnabled =
        !isDragging && interactionMode === CrosshairInteractionMode.Full;
    // Whether the root handles at the line endpoints accept mouse events.
    const isRootHandleEnabled =
        !isDragging &&
        (interactionMode === CrosshairInteractionMode.Full ||
            interactionMode === CrosshairInteractionMode.RootsOnly);
    // Whether to render lines with a visual indicator that they are
    // disabled. True whenever a drawing tool is active (both between
    // strokes and during a stroke) so the indicator does not flash on
    // and off during mouse-button-held periods.
    const showDisabledVisual =
        interactionMode === CrosshairInteractionMode.RootsOnly ||
        interactionMode === CrosshairInteractionMode.DrawingStroke;

    const [hoveredLine, setHoveredLine] = useState<
        'horizontal' | 'vertical' | 'center' | null
    >(null);

    // Clear visual hover state when line interaction becomes disallowed —
    // no onMouseLeave will fire because pointer-events switches to 'none'.
    useEffect(() => {
        if (!isLineEnabled) {
            setHoveredLine(null);
        }
    }, [isLineEnabled]);

    const isHorizontalDraggerWithinBoundaries =
        horizontal !== undefined && horizontal >= 0 && horizontal <= 100;
    const isVerticalDraggerWithinBoundaries =
        vertical !== undefined && vertical >= 0 && vertical <= 100;

    // Forward mousemove events through the crosshair overlay to the
    // canvas underneath so the HUD coordinate/HU display updates
    // continuously even when the cursor is over a hit-area element.
    const forwardMouseMoveToCanvas = useCallback(
        (event: React.MouseEvent<SVGElement, MouseEvent>) => {
            boxRef.current
                ?.closest('[data-viewport-container]')
                ?.querySelector('canvas')
                ?.dispatchEvent(new MouseEvent('mousemove', event.nativeEvent));
        },
        []
    );

    const handleMouseDown = (
        event: React.MouseEvent<HTMLDivElement | SVGElement, MouseEvent>,
        type: 'horizontal' | 'vertical' | 'both',
        isRootHandle = false
    ) => {
        event.preventDefault();

        const allowed = isRootHandle ? isRootHandleEnabled : isLineEnabled;
        if (!allowed) {
            return;
        }

        if (view?.type === PlaneViewType.Secondary && areEnvironmentsLocked) {
            enqueueSnackbar(<FormattedMessage id="crosshairMovementLocked" />, {
                variant: 'info',
            });
            return;
        }

        ToolManager.setCrosshairDragActive(true);
        const dragCursor =
            type === 'both'
                ? 'move'
                : type === 'horizontal'
                  ? 'ns-resize'
                  : 'ew-resize';
        isDraggingRef.current = true;
        setIsDragging(true);

        // Override the cursor on [data-viewport-container] and any
        // descendants that already have an inline cursor style (e.g.
        // the canvas parent set by ViewportCursor.setParentsCursorStyle).
        // Without this, those higher-priority elements override
        // document.body.style.cursor, causing cursor flicker on fast drags.
        const container = boxRef.current?.closest(
            '[data-viewport-container]'
        ) as HTMLElement | null;
        const containerCursorEls: {
            el: HTMLElement;
            originalCursor: string;
        }[] = [];
        if (container) {
            containerCursorEls.push({
                el: container,
                originalCursor: container.style.cursor,
            });
            container.querySelectorAll<HTMLElement>('*').forEach((el) => {
                if (el.style.cursor) {
                    containerCursorEls.push({
                        el,
                        originalCursor: el.style.cursor,
                    });
                }
            });
            containerCursorEls.forEach(({ el }) => {
                el.style.cursor = dragCursor;
            });
        }
        document.body.style.cursor = dragCursor;

        const startPos = { x: event.clientX, y: event.clientY };

        const onMouseMove = (moveEvent: MouseEvent) => {
            const { clientX, clientY } = moveEvent;
            let delta: number;

            if (type === 'both') {
                // Handle both axes simultaneously
                const deltaX = clientX - startPos.x;
                const deltaY = clientY - startPos.y;

                startPos.x = clientX;
                startPos.y = clientY;

                handleCrosshairChange('vertical', deltaX);
                handleCrosshairChange('horizontal', deltaY);
                return;
            }

            if (type === 'horizontal') {
                delta = clientY - startPos.y;
                startPos.y = clientY;
            } else {
                delta = clientX - startPos.x;
                startPos.x = clientX;
            }
            handleCrosshairChange(type as 'horizontal' | 'vertical', delta);
        };

        const abortController = new AbortController();
        dragAbortRef.current = abortController;

        const onMouseUp = () => {
            ToolManager.setCrosshairDragActive(false);
            isDraggingRef.current = false;
            setIsDragging(false);
            containerCursorEls.forEach(({ el, originalCursor }) => {
                el.style.cursor = originalCursor;
            });
            document.body.style.cursor = '';
            abortController.abort();
            dragAbortRef.current = null;
        };

        document.addEventListener('mousemove', onMouseMove, {
            signal: abortController.signal,
        });
        document.addEventListener('mouseup', onMouseUp, {
            signal: abortController.signal,
        });
    };

    // Line colour and opacity change when the drawing tool is active, to
    // signal to the user that full-line dragging is not available.
    const getLineStroke = (line: 'horizontal' | 'vertical'): string => {
        if (hoveredLine === line || hoveredLine === 'center') {
            return theme.palette.primary.main;
        }
        return showDisabledVisual ? theme.palette.grey[600] : 'gray';
    };
    const lineStroke = getLineStroke('horizontal');
    const verticalLineStroke = getLineStroke('vertical');
    const lineOpacity = showDisabledVisual ? 0.35 : 0.8;

    // Root handles appear as small circles at the four line endpoints.
    // They become visible when a drawing tool is active so the user can
    // see they are still interactive.
    const rootHandleOpacity = showDisabledVisual ? 0.75 : 0;
    const rootHandleRadius = 9;
    // Extra invisible pixels around each root handle to make it easier
    // to grab — a slightly larger hit zone without visual change.
    const rootHandleHitRadius = rootHandleRadius + 4;
    const rootHandleColor = theme.palette.primary.light;

    return (
        <Box
            ref={boxRef}
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 6,
            }}
        >
            {/* SVG for lines */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                }}
            >
                {isHorizontalDraggerWithinBoundaries && (
                    <>
                        {/* Invisible hit area for the full line */}
                        <line
                            x1="0%"
                            y1={`${horizontal}%`}
                            x2="100%"
                            y2={`${horizontal}%`}
                            stroke="transparent"
                            strokeWidth="10"
                            style={{
                                pointerEvents: isLineEnabled ? 'auto' : 'none',
                            }}
                            onMouseDown={(event) =>
                                handleMouseDown(event, 'horizontal')
                            }
                            onMouseMove={forwardMouseMoveToCanvas}
                            onMouseEnter={(event) => {
                                if (event.buttons === MOUSE_BUTTONS_NONE) {
                                    event.currentTarget.style.cursor =
                                        'ns-resize';
                                    setHoveredLine('horizontal');
                                }
                            }}
                            onMouseLeave={(event) => {
                                if (!isDraggingRef.current) {
                                    event.currentTarget.style.cursor = '';
                                    setHoveredLine(null);
                                }
                            }}
                        />
                        {/* Visible line */}
                        <line
                            x1="0%"
                            y1={`${horizontal}%`}
                            x2="100%"
                            y2={`${horizontal}%`}
                            stroke={lineStroke}
                            strokeWidth={
                                hoveredLine === 'horizontal' ||
                                hoveredLine === 'center'
                                    ? '2'
                                    : '1.5'
                            }
                            opacity={lineOpacity}
                            style={{ pointerEvents: 'none' }}
                        />
                        {/* Root handles — left and right endpoints */}
                        {['0%', '100%'].map((cx) => (
                            <React.Fragment key={cx}>
                                {/* Invisible expanded hit area */}
                                <circle
                                    cx={cx}
                                    cy={`${horizontal}%`}
                                    r={rootHandleHitRadius}
                                    fill="transparent"
                                    style={{
                                        pointerEvents: isRootHandleEnabled
                                            ? 'auto'
                                            : 'none',
                                        cursor: 'ns-resize',
                                    }}
                                    onMouseDown={(event) =>
                                        handleMouseDown(
                                            event,
                                            'horizontal',
                                            true
                                        )
                                    }
                                    onMouseMove={forwardMouseMoveToCanvas}
                                />
                                {/* Visible handle */}
                                <circle
                                    cx={cx}
                                    cy={`${horizontal}%`}
                                    r={rootHandleRadius}
                                    fill={rootHandleColor}
                                    opacity={rootHandleOpacity}
                                    style={{ pointerEvents: 'none' }}
                                />
                            </React.Fragment>
                        ))}
                    </>
                )}

                {isVerticalDraggerWithinBoundaries && (
                    <>
                        {/* Invisible hit area for the full line */}
                        <line
                            x1={`${vertical}%`}
                            y1="0%"
                            x2={`${vertical}%`}
                            y2="100%"
                            stroke="transparent"
                            strokeWidth="10"
                            style={{
                                pointerEvents: isLineEnabled ? 'auto' : 'none',
                            }}
                            onMouseDown={(event) =>
                                handleMouseDown(event, 'vertical')
                            }
                            onMouseMove={forwardMouseMoveToCanvas}
                            onMouseEnter={(event) => {
                                if (event.buttons === MOUSE_BUTTONS_NONE) {
                                    event.currentTarget.style.cursor =
                                        'ew-resize';
                                    setHoveredLine('vertical');
                                }
                            }}
                            onMouseLeave={(event) => {
                                if (!isDraggingRef.current) {
                                    event.currentTarget.style.cursor = '';
                                    setHoveredLine(null);
                                }
                            }}
                        />
                        {/* Visible line */}
                        <line
                            x1={`${vertical}%`}
                            y1="0%"
                            x2={`${vertical}%`}
                            y2="100%"
                            stroke={verticalLineStroke}
                            strokeWidth={
                                hoveredLine === 'vertical' ||
                                hoveredLine === 'center'
                                    ? '2'
                                    : '1.5'
                            }
                            opacity={lineOpacity}
                            style={{ pointerEvents: 'none' }}
                        />
                        {/* Root handles — top and bottom endpoints */}
                        {['0%', '100%'].map((cy) => (
                            <React.Fragment key={cy}>
                                {/* Invisible expanded hit area */}
                                <circle
                                    cx={`${vertical}%`}
                                    cy={cy}
                                    r={rootHandleHitRadius}
                                    fill="transparent"
                                    style={{
                                        pointerEvents: isRootHandleEnabled
                                            ? 'auto'
                                            : 'none',
                                        cursor: 'ew-resize',
                                    }}
                                    onMouseDown={(event) =>
                                        handleMouseDown(event, 'vertical', true)
                                    }
                                    onMouseMove={forwardMouseMoveToCanvas}
                                />
                                {/* Visible handle */}
                                <circle
                                    cx={`${vertical}%`}
                                    cy={cy}
                                    r={rootHandleRadius}
                                    fill={rootHandleColor}
                                    opacity={rootHandleOpacity}
                                    style={{ pointerEvents: 'none' }}
                                />
                            </React.Fragment>
                        ))}
                    </>
                )}

                {/* Centre intersection handle for dragging both axes */}
                {isHorizontalDraggerWithinBoundaries &&
                    isVerticalDraggerWithinBoundaries && (
                        <circle
                            cx={`${vertical}%`}
                            cy={`${horizontal}%`}
                            r={6}
                            fill={theme.palette.primary.main}
                            opacity={0}
                            stroke={theme.palette.primary.dark}
                            strokeWidth="1"
                            style={{
                                pointerEvents: isLineEnabled ? 'auto' : 'none',
                            }}
                            onMouseDown={(event) =>
                                handleMouseDown(event, 'both')
                            }
                            onMouseMove={forwardMouseMoveToCanvas}
                            onMouseEnter={(event) => {
                                if (event.buttons === MOUSE_BUTTONS_NONE) {
                                    event.currentTarget.style.cursor = 'move';
                                    setHoveredLine('center');
                                }
                            }}
                            onMouseLeave={(event) => {
                                if (!isDraggingRef.current) {
                                    event.currentTarget.style.cursor = '';
                                    setHoveredLine(null);
                                }
                            }}
                        />
                    )}
            </svg>
        </Box>
    );
};

export default Crosshairs;
